import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import type {
  LogicTypeStepWorkspaceReadModel,
  WorkspaceWorkspaceReadModel
} from '@r/api/generated/client.schemas.ts';
import { useApiAdminlogicTypeStepsIdPatch } from '@r/api/generated/client.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface UpdateStepFormProps {
  step: LogicTypeStepWorkspaceReadModel;
  onSuccess: () => void;
}

const STEP_POINT_NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

/** PATCH nom / description d’un palier ; le seuil de points reste en lecture seule. */
export const UpdateStepForm: FC<UpdateStepFormProps> = ({ step, onSuccess }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();

  const FormSchema = z.object({
    name: z
      .string()
      .min(1, t('form.error.required'))
      .max(50, t('form.error.char.max', { maxChar: 50 })),
    description: z
      .string()
      .max(1000, t('form.error.char.max', { maxChar: 1000 }))
      .optional()
  });

  type FormValues = z.infer<typeof FormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: step.name ?? '',
      description: step.description ?? ''
    }
  });

  const patchStepMutation = useApiAdminlogicTypeStepsIdPatch();
  const formattedStepPoint = STEP_POINT_NUMBER_FORMAT.format(step.stepPoint);

  function onSubmit(data: FormValues) {
    if (step.id == null) {
      toast.error(t('admin.settings.steps.update.error'));
      return;
    }

    const trimmedDescription = data.description?.trim();

    patchStepMutation.mutate(
      {
        id: String(step.id),
        data: {
          name: data.name,
          description: trimmedDescription ? trimmedDescription : null
        }
      },
      {
        onSuccess: (updatedStep) => {
          queryClient.setQueriesData<WorkspaceWorkspaceReadModel>(
            { queryKey: ['workspace'] },
            (workspace) => {
              const steps = workspace?.currentLogicWorkspace?.steps;
              if (!workspace?.currentLogicWorkspace || !steps?.length) {
                return workspace;
              }

              return {
                ...workspace,
                currentLogicWorkspace: {
                  ...workspace.currentLogicWorkspace,
                  steps: steps.map((s) =>
                    s.id === updatedStep.id
                      ? {
                          ...s,
                          name: updatedStep.name,
                          description: updatedStep.description
                        }
                      : s
                  )
                }
              };
            }
          );
          queryClient.invalidateQueries({ queryKey: ['workspace'] });
          toast.success(t('admin.settings.steps.update.success'));
          onSuccess();
        },
        onError: (error) => {
          if (isOrvalErrorFromPath(error, 'patch', '/admin/logicTypeSteps')) {
            showOrvalErrorToast(error, t('admin.settings.steps.update.error'));
          } else {
            toast.error(t('admin.settings.steps.update.error'));
          }
        }
      }
    );
  }

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('form.step.name.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('form.step.name.label')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('form.step.description.label')}</FormLabel>
              <FormControl>
                <Textarea
                  rows={4}
                  placeholder={t('form.step.description.label')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormItem>
          <FormLabel>{t('admin.settings.steps.form.stepPoint.label')}</FormLabel>
          <FormControl>
            <Input
              disabled
              readOnly
              value={formattedStepPoint}
              className="cursor-not-allowed bg-muted text-muted-foreground"
              aria-readonly
            />
          </FormControl>
        </FormItem>

        <Button
          rounded="full"
          loading={patchStepMutation.isPending}
          className="mt-2 w-full"
          type="submit"
        >
          {t('admin.settings.steps.update.form.submit')}
        </Button>
      </form>
    </Form>
  );
};
